import { Tooltip } from '@fullstory/components';
import { BulkActionResourceType, Metadata, ResourceDisplayPane } from '@fullstory/library-ui';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import React, { PropsWithChildren } from 'react';
import { ActivityMonitoringData } from '../../types';
import { ObjectDetailsContent } from '../ObjectDetailsContent';

interface ObjectDetailsTooltipContentProps {
  name: string;
  description: string;
  canEdit: boolean;
  showDataDirect: boolean;
  eventSupport: DQMRow.EventSupport;
  activityMonitoring: ActivityMonitoringData | null;
  objectType: BulkActionResourceType;
  metadata?: Metadata;
  onEdit?: () => void;
  isView?: boolean;
  extraContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  headerIcon?: React.ComponentProps<typeof ResourceDisplayPane.Header>['icon'];
}

export const ObjectDetailsTooltipContent: React.FC<ObjectDetailsTooltipContentProps> = ({
  metadata,
  canEdit,
  onEdit,
  eventSupport,
  isView,
  name,
  description,
  extraContent,
  objectType,
  activityMonitoring,
  footerContent,
  headerIcon,
  showDataDirect,
}) => {
  const lowercaseObjectType = React.useMemo(() => objectType.toLowerCase(), [objectType]);

  return (
    <ResourceDisplayPane data-component="ObjectDetails" tooltip staticHeight={false}>
      <ResourceDisplayPane.Header
        label={name}
        onOpen={onEdit}
        isView={isView}
        canAccess={canEdit}
        icon={headerIcon}
        testId={`${lowercaseObjectType}s-table-event-preview-edit-button`}
      />
      <ResourceDisplayPane.Body testId={`${lowercaseObjectType}s-table-event-preview`}>
        <ObjectDetailsContent
          description={description}
          showDataDirect={showDataDirect}
          eventSupport={eventSupport}
          activityMonitoring={activityMonitoring}
          extraContent={extraContent}
        />
      </ResourceDisplayPane.Body>
      {footerContent ?? (metadata && <ResourceDisplayPane.Footer metadata={metadata} />)}
    </ResourceDisplayPane>
  );
};

export interface ObjectDetailsTooltipProps {
  object: DQMRow;
  tooltipContent: (object: DQMRow, onEdit?: () => void) => React.ReactNode;
  onEdit?: () => void;
  className?: string;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
}

export const ObjectDetailsTooltip: React.FC<PropsWithChildren<ObjectDetailsTooltipProps>> = ({
  object,
  onEdit,
  children,
  className,
  placement,
  tooltipContent,
}) => (
  <Tooltip
    data-component="ObjectDetailsTooltip"
    disablePadding
    placement={placement || 'right'}
    maxWidth={400}
    className={className}
    showDelay={300}
    persistDelay={100}
    tooltipContent={tooltipContent(object, onEdit)}
    variant="light"
  >
    {children}
  </Tooltip>
);
